/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * ConfirmationCard
 *
 * @file  ConfirmationCard.jsx
 * @author Satyaki Bose
 * @since 1.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import mixins from '../../assets/styles/mixins.scss';
import SafeButtonWrapper from './SafeButtonWrapper';

const styles = () => ({
  paper: {
    padding: 48,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 24,
    width: 584,
    maxWidth: 800,
    borderRadius: 24,
    boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.1)',
    flexShrink: 0,
    boxSizing: 'border-box'
  },
  closeButton: {
    position: 'absolute',
    right: '16px',
    top: '16px',
    color: mixins.blackColor,
    fontSize: 18,
    width: 18,
    height: 18
  },
  title: {
    padding: 0
  },
  titleText: {
    fontSize: mixins.fontSize24,
    fontWeight: mixins.fontWeight500,
    letterSpacing: '-0.48px',
    fontStyle: 'normal',
    color: mixins.blackColor
  },
  content: {
    padding: 0,
    marginBottom: 0,
    gap: 18,
    display: 'flex',
    flexDirection: 'column'
  },
  contentText: {
    fontSize: mixins.fontSize16,
    lineHeight: '24px',
    color: mixins.color_text_primary
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    padding: 0,
    width: '100%',
    boxSizing: 'border-box',
    margin: 0
  },
  cancelButton: {
    borderRadius: '1000px',
    padding: 8,
    color: mixins.blackColor,
    backgroundColor: 'transparent',
    minWidth: 140,
    minHeight: 24,
    border: '2px solid #0D004D'
  },
  cancelButtonText: {
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    color: mixins.blackColor,
    fontFamily: mixins.fontFamilyPlusSans,
    textTransform: 'none'
  },
  confirmButton: {
    padding: 8,
    borderRadius: 1000,
    backgroundColor: mixins.amethyst,
    color: mixins.whiteFont,
    minWidth: 140,
    minHeight: 24,
    '&:hover': {
      backgroundColor: `${mixins.amethyst} !important`
    }
  },
  confirmButtonText: {
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    color: mixins.whiteFont,
    fontFamily: mixins.fontFamilyPlusSans,
    textTransform: 'none'
  }
});

const ConfirmationCard = ({
  open,
  onClose,
  title,
  content,
  cancelText,
  confirmText,
  onCancel,
  onConfirm,
  showCloseButton,
  className,
  titleClassName,
  contentClassName,
  actionsClassName,
  classes,
  isLoading
}) => {
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    if (onClose) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    if (onClose) {
      onClose();
    }
  };

  const renderContent = () => {
    if (typeof content === 'string') {
      return (
        <Typography className={classes.contentText} variant="body1">
          {content}
        </Typography>
      );
    }
    if (Array.isArray(content)) {
      return content.map((text, index) => (
        <Typography key={index} className={classes.contentText} variant="body1">
          {text}
        </Typography>
      ));
    }

    return content;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={className}
      classes={{
        paper: classes.paper
      }}
    >
      {showCloseButton && onClose && (
        <IconButton
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      )}
      <DialogTitle className={`${classes.title} ${titleClassName || ''}`}>
        <Typography className={classes.titleText} variant="h2">
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent className={`${classes.content} ${contentClassName || ''}`}>
        {renderContent()}
      </DialogContent>
      <DialogActions className={`${classes.actions} ${actionsClassName || ''}`}>
        <SafeButtonWrapper isLoading={isLoading}>
          <Button
            className={classes.cancelButton}
            onClick={handleCancel}
            variant="outlined"
          >
            <Typography className={classes.cancelButtonText} variant="button">
              {cancelText}
            </Typography>
          </Button>
        </SafeButtonWrapper>
        <SafeButtonWrapper isLoading={isLoading}>
          <Button
            className={classes.confirmButton}
            onClick={handleConfirm}
            variant="contained"
          >
            <Typography className={classes.confirmButtonText} variant="button">
              {confirmText}
            </Typography>
          </Button>
        </SafeButtonWrapper>
      </DialogActions>
    </Dialog>
  );
};

ConfirmationCard.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  title: PropTypes.string.isRequired,
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.node
  ]).isRequired,
  cancelText: PropTypes.string,
  confirmText: PropTypes.string,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  showCloseButton: PropTypes.bool,
  className: PropTypes.string,
  titleClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  actionsClassName: PropTypes.string,
  classes: PropTypes.object,
  isLoading: PropTypes.bool
};

ConfirmationCard.defaultProps = {
  onClose: null,
  cancelText: 'Cancel',
  confirmText: 'Confirm',
  onCancel: null,
  onConfirm: null,
  showCloseButton: true,
  className: '',
  titleClassName: '',
  contentClassName: '',
  actionsClassName: '',
  classes: {},
  isLoading: false
};

export default withStyles(styles)(ConfirmationCard);

